import React from 'react';

import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

export type GuideNumberedTimelineItem = Readonly<{
  id: string;
  number: number;
  title: string;
  /** Optional brand-blue chip rendered after the title, e.g. a grade level. */
  badge?: string;
  points: readonly string[];
}>;

type GuideNumberedTimelineProps = Readonly<{
  items: readonly GuideNumberedTimelineItem[];
}>;

/** Vertical timeline of numbered nodes connected by a line, each with a content card. */
export const GuideNumberedTimeline: React.FC<GuideNumberedTimelineProps> = ({ items }) => (
  <Box sx={timelineSx}>
    {items.map((item, index) => {
      const isLast = index === items.length - 1;

      return (
        <Box key={item.id} sx={rowSx(isLast)}>
          <Box sx={railSx}>
            <Box sx={nodeSx}>
              <Typography variant="text-sm-semibold" sx={nodeTextSx}>
                {item.number}
              </Typography>
            </Box>
            {!isLast && <Box sx={lineSx} />}
          </Box>
          <Box sx={cardSx}>
            <Box sx={headerSx}>
              <Typography variant="text-lg-semibold" sx={titleSx}>
                {item.title}
              </Typography>
              {item.badge && (
                <Chip
                  variant="badge"
                  size="sm"
                  type="default"
                  label={item.badge}
                  sx={badgeChipSx}
                />
              )}
            </Box>
            <Box sx={listSx}>
              {item.points.map((point) => (
                <Typography key={point} variant="text-sm-regular" sx={pointSx}>
                  {point}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      );
    })}
  </Box>
);

const timelineSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const rowSx =
  (isLast: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'stretch',
    gap: backrs.spacing['spacing-xl'],
    pb: isLast ? 0 : backrs.spacing['spacing-xl'],
  });

const railSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '32px',
  flexShrink: 0,
  // aligns the node with the card title, which sits below the card's top padding
  pt: `calc(${backrs.spacing['spacing-3xl']} - 2px)`,
});

const nodeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  flexShrink: 0,
  borderRadius: backrs.radius['radius-full'],
  border: `1px solid ${backrs.colors['Colors/Background/bg-primary-solid']}`,
  backgroundColor: backrs.colors['Brand/500'],
});

const nodeTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Base/White'],
});

const lineSx: SxProps<Theme> = ({ backrs }) => ({
  width: '2px',
  flex: 1,
  minHeight: backrs.spacing['spacing-xl'],
  // extends through the row gap and the next rail's offset so the line meets the next node
  mb: `calc(-1 * (${backrs.spacing['spacing-xl']} + ${backrs.spacing['spacing-3xl']} - 2px))`,
  backgroundColor: backrs.colors['Brand/500'],
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  p: backrs.spacing['spacing-3xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const badgeChipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  '& .MuiChip-label': {
    color: backrs.colors['Brand/700'],
  },
});

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const pointSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
